import { useEffect, useState } from 'react';

import { type HoursClient, HoursError } from './client.ts';
import { insideWorkingHours, summariseHours, type WorkingDay } from './hours.ts';

/** How a client reaches a colleague's hours: the service and the map from a
 *  chat user to the person id the hours are keyed on. */
export type ColleagueHoursSource = {
  client: HoursClient;
  personId: (chatUserId: string) => string | null;
};

/**
 * A colleague's working hours, read-only: the week, and whether they are inside
 * it right now. A person with no hours set draws nothing; hours that cannot be
 * read draw a quiet "Hours unavailable".
 */
export function ColleagueHours({
  source,
  chatUserId,
  now = () => new Date(),
}: {
  source: ColleagueHoursSource;
  chatUserId: string;
  now?: () => Date;
}) {
  const [days, setDays] = useState<readonly WorkingDay[]>([]);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const person = source.personId(chatUserId);
    if (!person) return;
    let live = true;
    setFailed(false);
    source.client
      .read(person)
      .then((found) => live && setDays(found))
      .catch((error: unknown) => {
        if (!live) return;
        setDays([]);
        if (!(error instanceof HoursError && error.status === 404)) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [source, chatUserId]);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (failed)
    return <span className="text-xs text-muted truncate hours-colleague">Hours unavailable</span>;
  if (days.length === 0) return null;
  void tick;
  const inside = insideWorkingHours(days, now());
  return (
    <span className="text-xs text-muted truncate hours-colleague">
      {summariseHours(days)} · {inside ? 'working now' : 'outside working hours'}
    </span>
  );
}
